import { useEffect, useState } from 'react'

import { fetchLocalJSON } from '../../../shared/utils/fetchLocalJSON.js'
import { Logger }         from '../../../shared/utils/logger.js'
import { FETCH_STATUS }   from '../../../shared/constants/fetchStatus.js'

const EMPTY_BUILDINGS = Object.freeze([])

/**
 * Loads and structurally normalizes the required building collection.
 *
 * The source file is `buildings/buildings.json` in the project data package and
 * is expected to contain a non-empty `buildings` array. Buildings are sorted by
 * `id_cad`; each building's nested `tiers` array is copied and sorted by
 * `ref_tier`. The source response is never mutated.
 *
 * This hook only owns the remote resource lifecycle and structural ordering.
 * It does not merge CMS phasing settings or derive inventory-dependent flags
 * such as `hasActiveTiers` and `isSoldOut`; those transformations belong to the
 * Inventory provider's pure derivation layer.
 *
 * Missing configuration, an invalid or empty `buildings` collection, or a
 * loading failure produces an empty array with `status` set to
 * `FETCH_STATUS.ERROR` and the failure reason exposed as `error`.
 *
 * A change of `dataUrl` resets the resource to its loading state and starts a
 * new request. Results from an obsolete request are ignored after cleanup.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.dataUrl - Base URL of the local project data package.
 * @returns {{
 *   data: Object[],
 *   status: 'loading'|'ready'|'error',
 *   error: Error|null,
 * }} Building data and its loading state. The returned object remains stable until the state changes.
 */
export function useBuildings({ dataUrl }) {
  const [state, setState] = useState({
    data: EMPTY_BUILDINGS,
    status: FETCH_STATUS.LOADING,
    error: null,
  })

  useEffect(() => {
    if (!dataUrl) {
      setState({
        data: EMPTY_BUILDINGS,
        status: FETCH_STATUS.ERROR,
        error: new Error('Project data URL is unavailable'),
      })
      Logger.error({
        message: 'Project data URL is unavailable'
      })
      return
    }

    let cancelled = false

    setState({
      data: EMPTY_BUILDINGS,
      status: FETCH_STATUS.LOADING,
      error: null,
    })

    fetchLocalJSON(
      `${dataUrl}buildings/buildings.json`,
      'InventoryContext: buildings.json',
    )
      .then(data => {
        if (cancelled) return

        if (!Array.isArray(data?.buildings) || data.buildings.length === 0) {
          setState({
            data: EMPTY_BUILDINGS,
            status: FETCH_STATUS.ERROR,
            error: new Error(
              'buildings.json is missing or does not contain a non-empty buildings array',
            ),
          })
          Logger.error({
            message: 'buildings.json is missing or has an invalid buildings array',
          })
          return
        }

        const buildingsSorted = [...data.buildings]
            .map(building => ({
            ...building,
            tiers: building.tiers 
                ? [...building.tiers].sort((a, b) => (a.ref_tier ?? 0) - (b.ref_tier ?? 0))
                : []
            }))
            .sort((a, b) => (a.id_cad ?? '').localeCompare(b.id_cad ?? ''));
        
        setState({
          data: buildingsSorted,
          status: FETCH_STATUS.READY,
          error: null,
        })
        Logger.info({message:'buildings.json loaded'})
      })
      .catch(error => {
        if (cancelled) return

        setState({
          data: EMPTY_BUILDINGS,
          status: FETCH_STATUS.ERROR,
          error: error instanceof Error
            ? error
            : new Error(String(error)),
          })
        Logger.error({message:'error loading buildings.json', content: error})
      })

    return () => {
      cancelled = true
    }
  }, [dataUrl])

  return state
}
